// scheduleGenerator.js
// Generates Priority Queue tasks from checklist schedules

/**
 * Generate next occurrence date for a checklist schedule
 */
export function getNextOccurrence(schedule, fromDate = new Date()) {
  const { frequency, daysOfWeek, dayOfMonth, monthlyPattern, weekOfMonth, dayOfWeekForMonthly, monthsOfYear, timeOfDay } = schedule;
  
  if (frequency === 'adhoc') {
    return null; // Manual only
  }
  
  const nextDate = new Date(fromDate);
  const [hours, minutes] = (timeOfDay || '09:00').split(':');
  nextDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
  
  if (frequency === 'daily') {
    // If time already passed today, use tomorrow
    if (nextDate <= fromDate) {
      nextDate.setDate(nextDate.getDate() + 1);
    }
    return nextDate;
  }
  
  if (frequency === 'weekly') {
    // Find next occurrence of selected days
    const today = nextDate.getDay() || 7; // 0=Sun -> 7, 1=Mon -> 1
    const selectedDays = (daysOfWeek || []).slice().sort((a, b) => a - b);
    
    for (let i = 0; i < 7; i++) {
      const checkDay = ((today + i - 1) % 7) + 1; // Convert to Mon=1, Sun=7
      if (selectedDays.includes(checkDay)) {
        if (i === 0 && nextDate > fromDate) {
          return nextDate; // Today, time hasn't passed
        }
        nextDate.setDate(nextDate.getDate() + i);
        return nextDate;
      }
    }
  }
  
  if (frequency === 'monthly') {
    if (monthlyPattern === 'date') {
      // Specific date (e.g., 15th)
      nextDate.setDate(dayOfMonth || 1);
      if (nextDate <= fromDate) {
        nextDate.setMonth(nextDate.getMonth() + 1);
        nextDate.setDate(dayOfMonth || 1);
      }
      return nextDate;
    } else {
      // Specific weekday (e.g., 2nd Tuesday)
      return getNthWeekdayOfMonth(nextDate, weekOfMonth, dayOfWeekForMonthly);
    }
  }
  
  if (frequency === 'quarterly') {
    const currentMonth = nextDate.getMonth() + 1;
    const months = monthsOfYear || [];
    const nextMonth = months.find(m => m > currentMonth) || months[0];
    
    if (!nextMonth) return null;
    if (nextMonth <= currentMonth) {
      nextDate.setFullYear(nextDate.getFullYear() + 1);
    }
    nextDate.setMonth(nextMonth - 1);
    
    if (monthlyPattern === 'date') {
      nextDate.setDate(dayOfMonth || 1);
    } else {
      return getNthWeekdayOfMonth(nextDate, weekOfMonth, dayOfWeekForMonthly);
    }
    return nextDate;
  }
  
  if (frequency === 'semiannual') {
    const currentMonth = nextDate.getMonth() + 1;
    const months = monthsOfYear || [];
    const nextMonth = months.find(m => m > currentMonth) || months[0];
    
    if (!nextMonth) return null;
    if (nextMonth <= currentMonth) {
      nextDate.setFullYear(nextDate.getFullYear() + 1);
    }
    nextDate.setMonth(nextMonth - 1);
    
    if (monthlyPattern === 'date') {
      nextDate.setDate(dayOfMonth || 1);
    } else {
      return getNthWeekdayOfMonth(nextDate, weekOfMonth, dayOfWeekForMonthly);
    }
    return nextDate;
  }
  
  if (frequency === 'annual') {
    const months = monthsOfYear || [];
    const month = months[0];
    if (!month) return null;
    nextDate.setMonth(month - 1);
    
    if (nextDate <= fromDate) {
      nextDate.setFullYear(nextDate.getFullYear() + 1);
    }
    
    if (monthlyPattern === 'date') {
      nextDate.setDate(dayOfMonth || 1);
    } else {
      return getNthWeekdayOfMonth(nextDate, weekOfMonth, dayOfWeekForMonthly);
    }
    return nextDate;
  }
  
  return null;
}

/**
 * Get Nth weekday of month (e.g., 2nd Tuesday)
 */
function getNthWeekdayOfMonth(date, week, dayOfWeek) {
  const result = new Date(date);
  result.setDate(1); // Start of month
  
  // Find first occurrence of the target day
  const firstDay = result.getDay() || 7; // 0=Sun -> 7
  const targetDay = dayOfWeek;
  
  let daysToAdd = targetDay - firstDay;
  if (daysToAdd < 0) daysToAdd += 7;
  
  result.setDate(1 + daysToAdd);
  
  // Add weeks
  const weekOffset = {
    'first': 0,
    'second': 7,
    'third': 14,
    'fourth': 21,
    'last': -1 // Special handling
  };
  
  if (week === 'last') {
    // Find last occurrence
    const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0);
    let lastOccurrence = result;
    while (result <= lastDay) {
      if (result.getDay() === (dayOfWeek === 7 ? 0 : dayOfWeek)) {
        lastOccurrence = new Date(result);
      }
      result.setDate(result.getDate() + 7);
    }
    return lastOccurrence;
  } else {
    result.setDate(result.getDate() + (weekOffset[week] || 0));
  }
  
  return result;
}

/**
 * Generate Priority Queue item from checklist
 */
export function generateQueueItemFromChecklist(checklist) {
  const nextOccurrence = getNextOccurrence(checklist.schedule);
  
  if (!nextOccurrence) {
    return null; // Ad-hoc, no automatic generation
  }
  
  const estimatedTime = Math.max(5, (checklist.questions || []).length * 2); // 2 min per question, min 5
  
  return {
    type: 'checklist',
    checklistId: checklist.id,
    checklistName: checklist.name,
    category: 'Checklist',
    title: checklist.name,
    description: checklist.description,
    dueDate: nextOccurrence.toISOString(),
    estimatedTime,
    priority: checklist.schedule.priority,
    status: 'pending',
    createdAt: new Date().toISOString(),
    assignedTo: 'current-user',
    questions: checklist.questions || []
  };
}

/**
 * Format due date for display
 */
export function formatDueDate(isoDate) {
  const date = new Date(isoDate);
  const now = new Date();
  
  const isToday = date.toDateString() === now.toDateString();
  const isTomorrow = date.toDateString() === new Date(now.getTime() + 86400000).toDateString();
  
  const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  
  if (isToday) return `Today at ${timeStr}`;
  if (isTomorrow) return `Tomorrow at ${timeStr}`;
  
  const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${dateStr} at ${timeStr}`;
}

/**
 * Check if task is overdue
 */
export function isOverdue(isoDate) {
  return new Date(isoDate) < new Date();
}

/**
 * Get priority color
 */
export function getPriorityColor(priority) {
  const colors = {
    critical: 'red',
    high: 'orange',
    medium: 'yellow',
    low: 'green'
  };
  return colors[priority] || 'gray';
}

/**
 * Get priority badge classes
 */
export function getPriorityBadge(priority) {
  const badges = {
    critical: 'bg-red-100 text-red-800 border-red-300',
    high: 'bg-orange-100 text-orange-800 border-orange-300',
    medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    low: 'bg-green-100 text-green-800 border-green-300'
  };
  return badges[priority] || 'bg-gray-100 text-gray-800 border-gray-300';
}
